import type { ApiQueryParams, DefaultQueryParams } from '@frontend/api-utils';
import { fetchAllPages } from '@frontend/api-utils';
import { SlotRendering } from '@frontend/common-types';
import { useAccountRepository } from '@frontend/repository';
import { SlotWorkflowClient } from '@frontend/slot/api';
import type { AvailableSlotConstraint, SlotQueryParams } from '@frontend/slot/types';
import { SlotStatus, SlotType } from '@frontend/slot/types';
import { SpotClient } from '@frontend/spot/api';
import { Spot } from '@frontend/spot/types';
import { useCallback, useEffect, useRef, useState } from 'react';

import { getSlotColor as getDefaultSlotColor, getSlotLabel } from '../../../spot-rendering/utils';

interface UseSelectLocationSpotLayoutProps {
    spotId: string | null;
    slotId: string | null;
    availableSlotConstraints: AvailableSlotConstraint;
    setSlotId: (value: string | null) => void;
}

export const useSelectLocationSpotLayout = (props: UseSelectLocationSpotLayoutProps) => {
    const { currentAccount } = useAccountRepository();
    const [selectedSpot, setSelectedSpot] = useState<Spot | null>(null);
    const [availableSlotIds, setAvailableSlotIds] = useState<Set<string>>(new Set());
    const [isLoadingSpotLayout, setIsLoadingSpotLayout] = useState(false);
    const layoutRequestId = useRef(0);

    useEffect(() => {
        if (!props.spotId) {
            setSelectedSpot(null);
            return;
        }

        let cancelled = false;
        SpotClient.resolveSpots([props.spotId]).then((response) => {
            if (!cancelled) {
                setSelectedSpot(response.results[0] ?? null);
            }
        });

        return () => {
            cancelled = true;
        };
    }, [props.spotId]);

    useEffect(() => {
        const accountId = currentAccount;
        const spotId = props.spotId;

        if (!accountId || !spotId) {
            setAvailableSlotIds(new Set());
            setIsLoadingSpotLayout(false);
            return;
        }

        const requestId = ++layoutRequestId.current;
        setIsLoadingSpotLayout(true);

        const constraints = {
            ...props.availableSlotConstraints,
            spot_id: spotId
        };

        fetchAllPages(
            (queryParams: ApiQueryParams<DefaultQueryParams & SlotQueryParams>) =>
                SlotWorkflowClient.postAvailableSlots(accountId, spotId, constraints, queryParams),
            { spot_id: spotId, status: SlotStatus.AVAILABLE }
        )
            .then((slots) => {
                if (requestId !== layoutRequestId.current) return;
                setAvailableSlotIds(new Set(slots.map((slot) => slot.id)));
            })
            .catch(() => {
                if (requestId !== layoutRequestId.current) return;
                setAvailableSlotIds(new Set());
            })
            .finally(() => {
                if (requestId === layoutRequestId.current) {
                    setIsLoadingSpotLayout(false);
                }
            });
    }, [currentAccount, props.availableSlotConstraints, props.spotId]);

    const isLayoutSlotDisabled = useCallback((slot: SlotRendering) => slot.type === SlotType.TERMINAL || !availableSlotIds.has(slot.id), [availableSlotIds]);

    const getLayoutSlotColor = useCallback(
        (slot: SlotRendering) => {
            if (slot.id === props.slotId) {
                return 'bg-status-success';
            }

            if (isLayoutSlotDisabled(slot)) {
                return 'bg-brand-disabled';
            }

            return getDefaultSlotColor(slot);
        },
        [isLayoutSlotDisabled, props.slotId]
    );

    const handleLayoutSlotClick = useCallback(
        (slot: SlotRendering) => {
            if (isLayoutSlotDisabled(slot)) return;
            void props.setSlotId(slot.id);
        },
        [isLayoutSlotDisabled, props.setSlotId]
    );

    return {
        selectedSpot,
        currentAccount,
        isLoadingSpotLayout,
        isLayoutSlotDisabled,
        getLayoutSlotColor,
        handleLayoutSlotClick,
        getSlotLabel
    };
};
